// src/components/auth/Register.jsx
//
// Public /register is now a "Choose your path" splash, not a self-serve
// account creator. Why: self-serve Channel Owner and Investor signups are
// fraud vectors. Channel Owners get curatorial power that can break the
// shared revenue for an entire Common Channel; Investor accounts touch a
// private placement product that legally cannot be publicly solicited.
//
// What this page does:
//   - Creator → /apply (5-step screening application)
//   - Channel Owner → /partners (curatorial inquiry, admin-reviewed)
//   - Investor → /invest-info (private placement interest form)
//   - Already have an account → /login
//
// The old self-serve form remains available to admins via /admin/users/new.
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Card, Typography, Button, Row, Col, Space, Tag,
} from 'antd';
import {
  UserAddOutlined, TeamOutlined, DollarCircleOutlined,
  LoginOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const heroStyle = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  padding: '56px 24px',
  marginBottom: -40,
};

const cardStyle = {
  borderRadius: 14,
  boxShadow: '0 4px 14px rgba(102,126,234,0.10)',
  height: '100%',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
};

const roleIconWrap = (color) => ({
  width: 56,
  height: 56,
  borderRadius: 14,
  background: color,
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 26,
  marginBottom: 16,
});

const Register = () => (
  <>
    {/* Hero */}
    <div style={heroStyle}>
      <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
        <Title level={1} style={{ color: 'white', marginBottom: 8, fontWeight: 800 }}>
          Join Choice MCN
        </Title>
        <Paragraph
          style={{ color: 'rgba(255,255,255,0.92)', fontSize: 16, margin: 0 }}
        >
          Pick the path that fits you. Every role has its own screening
          process so the right people end up in the right seats.
        </Paragraph>
      </div>
    </div>

    {/* Role cards */}
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 24px 64px' }}>
      <Row gutter={[24, 24]}>
        {/* Creator */}
        <Col xs={24} md={8}>
          <Card hoverable style={cardStyle}>
            <div style={roleIconWrap('linear-gradient(135deg,#667eea,#764ba2)')}>
              <UserAddOutlined />
            </div>
            <Title level={4}>I'm a Creator</Title>
            <Paragraph type="secondary">
              You make videos and want a home that handles monetisation,
              KYC, and payouts so you can focus on creating.
            </Paragraph>
            <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Self-serve application
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Review in 5–7 business days
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ 70–80% creator share on Common Channels
              </Text>
            </Space>
            <Link to="/apply">
              <Button type="primary" block size="large">
                Apply as Creator
              </Button>
            </Link>
          </Card>
        </Col>

        {/* Channel Owner */}
        <Col xs={24} md={8}>
          <Card hoverable style={cardStyle}>
            <div style={roleIconWrap('linear-gradient(135deg,#f59e0b,#d97706)')}>
              <TeamOutlined />
            </div>
            <Space size={6} style={{ marginBottom: 6 }}>
              <Title level={4} style={{ marginBottom: 0 }}>I'm a Channel Owner</Title>
              <Tag color="gold">By invitation</Tag>
            </Space>
            <Paragraph type="secondary">
              You'd run one of Choice's Common Channels — set the bar,
              curate creators, share in the upside.
            </Paragraph>
            <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Trust position — strict screening
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Clean YouTube history required
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Top creators can be promoted into this role
              </Text>
            </Space>
            <Link to="/partners">
              <Button block size="large">
                Apply to be a Channel Owner
              </Button>
            </Link>
          </Card>
        </Col>

        {/* Investor */}
        <Col xs={24} md={8}>
          <Card hoverable style={cardStyle}>
            <div style={roleIconWrap('linear-gradient(135deg,#10b981,#059669)')}>
              <DollarCircleOutlined />
            </div>
            <Space size={6} style={{ marginBottom: 6 }}>
              <Title level={4} style={{ marginBottom: 0 }}>I'm an Investor</Title>
              <Tag color="gold">Private placement</Tag>
            </Space>
            <Paragraph type="secondary">
              You're interested in our private-placement opportunities.
              Not publicly offered; by introduction only.
            </Paragraph>
            <Space direction="vertical" size={4} style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ No public solicitation
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ Acquired Movie Catalogue + select pots
              </Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                ✓ 1-on-1 follow-up if a fit
              </Text>
            </Space>
            <Link to="/invest-info">
              <Button block size="large">
                Register Interest
              </Button>
            </Link>
          </Card>
        </Col>
      </Row>

      {/* Existing user */}
      <Card style={{ marginTop: 24, borderRadius: 14, textAlign: 'center' }}>
        <Space size={12} wrap>
          <SafetyCertificateOutlined style={{ fontSize: 22, color: '#6d28d9' }} />
          <Text>Already have a Choice MCN account?</Text>
          <Link to="/login">
            <Button type="link" icon={<LoginOutlined />}>
              Sign in
            </Button>
          </Link>
        </Space>
      </Card>
    </div>
  </>
);

export default Register;
